import { EMAIL_SENDER } from '../../constantsConfig/constantsConfig';
import type { ILoginService } from '../../types';
import type {
  VerifyUserResponse,
  GetContactsResponse,
  SendSmsRequest,
  SendEmailRequest,
  LoginFinalResponse,
  ListBusinessLinksResponse,
} from '../../types/apiType';
import { encryptLoginCpf, encryptLoginUserKey } from '../../utils/loginEncryptUtils';
import { GIBS_ROUTES, LOGIN_ROUTES } from '../requestsService';
import { logEntraDeployDebug } from '../env';

export class LoginService implements ILoginService {
  private fetch: <T = unknown>(endpoint: string, options?: RequestInit) => Promise<T>;

  constructor(fetch: <T = unknown>(endpoint: string, options?: RequestInit) => Promise<T>) {
    this.fetch = fetch;
  }

  /**
   * Verify user with login and password (params in query)
   * @param login - User login (CPF)
   * @param senha - User password
   * @returns Promise<VerifyUserResponse> - Response with idPessoa
   */
  async verifyUser(login: string, senha: string): Promise<VerifyUserResponse> {
    logEntraDeployDebug();

    const [encryptedLogin, encryptedSenha] = await Promise.all([
      encryptLoginCpf(login),
      encryptLoginUserKey(senha),
    ]);
    const query = new URLSearchParams({ login: encryptedLogin, senha: encryptedSenha });

    const response = await this.fetch<VerifyUserResponse>(
      `${GIBS_ROUTES.POST_VERIFY_USER}?${query.toString()}`,
      { method: 'POST' }
    );

    return response;
  }

  /**
   * Get contacts of the user (body: idPessoa)
   * @param idPessoa - ID of the user
   * @returns Promise<GetContactsResponse> - Response with list of contacts
   */
  async getContacts(idPessoa: string): Promise<GetContactsResponse> {
    const response = await this.fetch<GetContactsResponse>(LOGIN_ROUTES.POST_GET_CONTACTS, {
      method: 'POST',
      body: JSON.stringify({ cpf: '', idPessoa, cnpj: '' }),
    });

    return response;
  }

  /**
   * Send SMS with verification code
   * @param to - Destination phone number
   * @param msg - Message with code
   * @returns Promise<void>
   */
  async sendSms(to: string, msg: string): Promise<void> {
    const request: SendSmsRequest = { to, msg };

    await this.fetch<void>(LOGIN_ROUTES.POST_SEND_SMS, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Send email with verification code.
   * The body always includes the keys from, to, subject, message, content and encoding (even if empty).
   * @param request - Email data (content and encoding optional; if omitted, sent as empty string)
   * @returns Promise<void>
   */
  async sendEmail(request: SendEmailRequest): Promise<void> {
    const body = {
      from: request.from ?? EMAIL_SENDER,
      to: request.to ?? '',
      subject: request.subject ?? 'Confirmação',
      message: request.message ?? '',
      content: request.content ?? '',
      encoding: request.encoding ?? 'base64',
    };
    await this.fetch<void>(LOGIN_ROUTES.POST_SEND_EMAIL, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Final login after code validation
   * @param cpf - CPF do usuário
   * @returns Promise<LoginFinalResponse> - Response with complete user data
   */
  async loginFinal(cpf: string): Promise<LoginFinalResponse> {
    const cleanCpf = cpf.replace(/\D/g, ''); // Remove formatting from CPF

    const response = await this.fetch<LoginFinalResponse>(LOGIN_ROUTES.POST_LOGIN_FINAL, {
      method: 'POST',
      body: JSON.stringify({ cpf: cleanCpf }),
    });

    return response;
  }

  /**
   * Lista vínculos de negócio por pessoa (POST /cadp/listar-vinculo-negocio/filtrados?idPessoa=).
   */
  async postListBusinessLinksByPessoaId(idPessoa: string): Promise<ListBusinessLinksResponse> {
    const search = new URLSearchParams({ idPessoa });
    const url = `${LOGIN_ROUTES.POST_LIST_BUSINESS_LINKS}?${search.toString()}`;
    return this.fetch<ListBusinessLinksResponse>(url, { method: 'POST' });
  }
}
